import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  PROFILE_BLOCK,
  VEHICLES,
  WORK_TIMES,
} from '../../../../services/utilityFunctions';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import FormGroup from '../../../../commonComponents/forms/FormGroup';
import Checkbox from '../../../../commonComponents/forms/inputs/Checkbox';
import ProfileField from './profile/ProfileField';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getValidationRules } from '../../helpers/helper';

function stateFromProps(member = {}) {
  const softSkills = member.softSkills || {};
  return {
    careerWishes: softSkills.careerWishes || '',
    others: softSkills.others || '',
    workTime: softSkills.workTime || '',
    vehicle: softSkills.vehicle || '',
    mobileAbroad: !!softSkills.mobileAbroad,
  };
}

/**
 * Form to update the current logged user
 */
function CareerForm({ t, member, onMemberChange, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};

  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'careerForm';
  const validationRules = useMemo(() => {
    const validation = {
      email: { required: true, email: true },
    };
    return getValidationRules(
      requiredFieldsPerBlock,
      PROFILE_BLOCK.CAREER,
      validation,
    );
  }, [getValidationRules, requiredFieldsPerBlock]);

  const handleSubmit = async () => {
    const params = {
      careerWishes: state.careerWishes,
      others: state.others,
      workTime: state.workTime,
      vehicle: state.vehicle,
      mobileAbroad: state.mobileAbroad,
    };
    onMemberChange(params, 'softSkills');
  };

  const handleChange = useCallback((field, value) => {
    setState(prevState => ({ ...prevState, [field]: value }));
  }, []);

  const inputProps = useCallback(field => {
    switch (field) {
      case 'workTime':
        return {
          items: WORK_TIMES.map(wt => ({
            id: wt,
            text: t(`profile_workTime_${wt}`),
          })),
        };
      case 'vehicle':
        return {
          items: VEHICLES.map(v => ({
            id: v,
            text: t(`profile_vehicle_${v}`),
          })),
        };
      default:
        return undefined;
    }
  }, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_career')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="careerWishes"
              type="textarea"
              value={state.careerWishes}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.CAREER}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="others"
              type="textarea"
              value={state.others}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.CAREER}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="workTime"
              type="select"
              value={state.workTime}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.CAREER}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="vehicle"
              type="select"
              value={state.vehicle}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.CAREER}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <FormGroup className="row">
              <Checkbox
                id="mobileAbroad"
                name="mobileAbroad"
                label={t('profile_mobileAbroad')}
                value={state.mobileAbroad}
                onChange={({ currentTarget }) =>
                  handleChange('mobileAbroad', currentTarget.checked)
                }
                className="offset-2"
              />
            </FormGroup>
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

CareerForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

CareerForm.defaultProps = {
  onSkipStep: null,
};

export default translate(CareerForm);
